import { FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { signUp, signInWithRedirect } from "../amplify";
import PublicShell from "../layout/PublicShell";
import { Button } from "../components/ui/Button";
import { Input, Label } from "../components/ui";
import { motion } from "framer-motion";
import { ArrowRight, Send } from "lucide-react";

export default function Signup() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);

  async function onGoogle() {
    setErr(null);
    try {
      await signInWithRedirect({ provider: "Google" });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Google sign-in failed");
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await signUp({
        username: email,
        password,
        options: { userAttributes: { email, given_name: firstName } },
      });
      nav(`/verify?email=${encodeURIComponent(email)}`, { replace: true });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Sign-up failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <PublicShell minimal>
      <div className="max-w-md mx-auto px-6 py-20">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
            begin your ilmli
          </div>
          <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
            Say <span className="italic text-terracotta">hallo.</span>
          </h1>
          <p className="mt-2 font-serif italic text-ink-soft text-base">
            ilmli lives in Telegram. One tap and you're in.
          </p>

          {/* Telegram-first tease — disabled until magic-link backend lands */}
          <button
            type="button"
            disabled
            title="Telegram signup coming soon — use Google or email below for now"
            className="mt-7 w-full flex items-center justify-center gap-2 rounded-md bg-ink/90 text-paper font-medium px-5 py-3.5 text-[15px] cursor-not-allowed opacity-90"
          >
            <Send size={16} />
            Continue with Telegram
            <span className="ml-2 font-mono text-[10px] tracking-wider uppercase text-paper/55">
              soon
            </span>
          </button>
          <p className="mt-2 font-mono text-[11px] text-ink-faint">
            no password to remember · we never message you outside Telegram
          </p>

          {/* Google — works today */}
          <Button
            variant="outline"
            onClick={onGoogle}
            className="w-full justify-center mt-6"
          >
            Continue with Google <ArrowRight size={14} />
          </Button>

          {/* Email — disclosure */}
          <button
            type="button"
            onClick={() => setEmailOpen((o) => !o)}
            className="mt-5 text-[13px] text-ink-soft underline decoration-ink-faint/40 underline-offset-4 hover:decoration-terracotta hover:text-terracotta"
          >
            {emailOpen ? "▾ hide email signup" : "▸ Use email instead"}
          </button>

          {emailOpen && (
            <motion.form
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.25 }}
              onSubmit={onSubmit}
              className="overflow-hidden space-y-4 mt-4"
            >
              <div>
                <Label>First name</Label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={10}
                />
                <p className="text-xs text-ink-faint mt-1.5">
                  At least 10 characters with letters and numbers.
                </p>
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="w-full justify-center"
              >
                {busy ? "Creating…" : "Create account"}
              </Button>
              <p className="text-xs text-ink-faint">
                Choose one method and stick with it — Google and email accounts
                don't merge later.
              </p>
            </motion.form>
          )}

          {err && (
            <div className="mt-4 text-sm text-terracotta font-medium">{err}</div>
          )}

          <p className="text-sm text-ink-soft mt-10">
            Already have an account?{" "}
            <Link
              to="/login"
              className="text-terracotta font-medium hover:underline"
            >
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </PublicShell>
  );
}
